import PropTypes from 'prop-types';
import React from 'react';

// material-ui
import { makeStyles, useTheme } from '@mui/styles';
import { Box, Drawer, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BrowserView, MobileView } from 'react-device-detect';

// project imports
import MenuList from './MenuList';
import LogoSection from '../LogoSection';
import SupportLinks from './Support';
import { drawerWidth } from 'store/constant';

// style constant
const useStyles = makeStyles((theme) => ({
    drawer: {
        [theme.breakpoints.up('lg')]: {
            width: drawerWidth,
            flexShrink: 0
        }
    },
    drawerPaper: {
        width: drawerWidth,
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.84) 0%, rgba(240, 247, 255, 0.78) 48%, rgba(240, 231, 255, 0.76) 100%)',
        backdropFilter: 'blur(26px)',
        color: theme.palette.text.primary,
        borderRight: '1px solid rgba(15, 140, 255, 0.14)',
        boxShadow: '18px 0 60px rgba(7, 17, 31, 0.08)',
        [theme.breakpoints.up('lg')]: {
            top: '76px'
        },
        [theme.breakpoints.down('lg')]: {
            top: '64px'
        },
        [theme.breakpoints.down('sm')]: {
            top: '56px',
            width: '100%',
            background: 'rgba(255, 255, 255, 0.94)'
        }
    },
    ScrollHeight: {
        height: 'calc(100vh - 76px)',
        paddingLeft: '20px',
        paddingRight: '20px',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('lg')]: {
            height: 'calc(100vh - 64px)'
        },
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100vh - 56px)',
            paddingLeft: '16px',
            paddingRight: '16px'
        }
    },
    boxContainer: {
        display: 'flex',
        padding: '20px',
        marginLeft: 'auto',
        marginRight: 'auto',
        [theme.breakpoints.down('sm')]: {
            padding: '16px'
        }
    },
    sidebarContent: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    menuSection: {
        flex: 1,
        minHeight: 0
    }
}));

// ===========================|| SIDEBAR DRAWER ||=========================== //

const Sidebar = ({ drawerOpen, drawerToggle, window }) => {
    const classes = useStyles();
    const theme = useTheme();
    const matchUpLg = useMediaQuery(theme.breakpoints.up('lg'));
    const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));

    const drawer = (
        <>
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                <div className={classes.boxContainer}>
                    <LogoSection />
                </div>
            </Box>
            <BrowserView>
                <div className={classes.ScrollHeight}>
                    <div className={classes.sidebarContent}>
                        <PerfectScrollbar component="div" className={classes.menuSection}>
                            <MenuList />
                            <SupportLinks />
                        </PerfectScrollbar>
                    </div>
                </div>
            </BrowserView>
            <MobileView>
                <Box
                    sx={{
                        px: matchDownSm ? 2 : 3,
                        py: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: matchDownSm ? 'calc(100vh - 56px)' : 'calc(100vh - 64px)'
                    }}
                >
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <MenuList />
                        <SupportLinks />
                    </Box>
                </Box>
            </MobileView>
        </>
    );

    const container = window !== undefined ? () => window().document.body : undefined;

    return (
        <nav className={classes.drawer} aria-label="mailbox folders">
            <Drawer
                container={container}
                variant={matchUpLg ? 'persistent' : 'temporary'}
                anchor="left"
                open={drawerOpen}
                onClose={drawerToggle}
                classes={{
                    paper: classes.drawerPaper
                }}
                ModalProps={{
                    keepMounted: true,
                    disableScrollLock: false
                }}
                color="inherit"
                sx={{
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: matchDownSm ? '100%' : drawerWidth,
                        maxWidth: matchDownSm ? '100%' : drawerWidth,
                        background:
                            'linear-gradient(180deg, rgba(255, 255, 255, 0.84) 0%, rgba(240, 247, 255, 0.78) 48%, rgba(240, 231, 255, 0.76) 100%)',
                        backdropFilter: 'blur(26px)',
                        borderRight: '1px solid rgba(15, 140, 255, 0.14)',
                        boxShadow: '18px 0 60px rgba(7, 17, 31, 0.08)'
                    }
                }}
            >
                {drawer}
            </Drawer>
        </nav>
    );
};

Sidebar.propTypes = {
    drawerOpen: PropTypes.bool,
    drawerToggle: PropTypes.func,
    window: PropTypes.object
};

export default Sidebar;
